import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleMinus,
} from '@rossum/ui/icons/tabler';
import {
  alpha,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { createFileSignature, isValidFile } from './helpers';

type Props = {
  fileList: File[];
  removeFileFromList: (signature: string) => void;
  selectedQueue: Queue | undefined;
};

export const FileList = ({
  fileList,
  removeFileFromList,
  selectedQueue,
}: Props) => {
  const intl = useIntl();
  const acceptedMimeTypes = selectedQueue?.settings.acceptedMimeTypes;

  const filesWithValidity = useMemo(() => {
    const fileListWithValidity = fileList.map(file => ({
      file,
      valid: isValidFile(file, acceptedMimeTypes),
    }));
    // When validity changes, we want the invalid files to be at the top of the list, so that user is aware of these
    return R.sortBy(fileListWithValidity, file => file.valid);
  }, [acceptedMimeTypes, fileList]);

  const validFilesLength = useMemo(
    () => filesWithValidity.filter(({ valid }) => valid),
    [filesWithValidity]
  ).length;

  return (
    <Stack flex={0}>
      <Stack direction="row" gap={0.5}>
        <Typography variant="body2" color="secondary.main" mb={1}>
          {intl.formatMessage({
            id: 'containers.annotationList.upload.fileList.selected',
          })}
        </Typography>
        <Typography variant="body2" mb={1}>
          {validFilesLength}
        </Typography>
      </Stack>
      <List
        sx={{
          maxHeight: '25vh',
          overflow: 'auto',
          alignSelf: 'stretch',
        }}
        disablePadding
      >
        {filesWithValidity.map(({ file, valid }) => (
          <ListItem
            key={createFileSignature(file)}
            sx={{
              py: 0.5,
              my: 1,
              backgroundColor: theme =>
                alpha(theme.palette.action.selected, 0.12),
              borderRadius: borderRadius('s'),
            }}
          >
            <ListItemText
              primary={file.name}
              primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 700 }}
            />
            <ListItemText
              primary={
                valid
                  ? intl.formatMessage({
                      id: 'containers.annotationList.upload.fileList.selected.success',
                    })
                  : intl.formatMessage({
                      id: 'containers.annotationList.upload.fileList.selected.error',
                    })
              }
              primaryTypographyProps={{
                variant: 'subtitle2',
                fontWeight: 400,
                sx: {
                  color: theme =>
                    valid
                      ? theme.palette.success.main
                      : theme.palette.warning.main,
                },
              }}
              sx={{ textAlign: 'end' }}
            />
            <Stack
              sx={{ ml: 1 }}
              spacing={2}
              direction="row"
              alignItems="center"
            >
              {valid ? (
                <SvgIcon fontSize="large" color="success">
                  <IconCheck />
                </SvgIcon>
              ) : (
                <Tooltip
                  title={intl.formatMessage(
                    {
                      id: 'containers.annotationList.upload.fileList.select.error.tooltip',
                    },
                    {
                      fileType: `${file.type}`,
                    }
                  )}
                >
                  <SvgIcon fontSize="large" color="warning">
                    <IconAlertTriangle />
                  </SvgIcon>
                </Tooltip>
              )}
              <IconButton
                onClick={() => removeFileFromList(createFileSignature(file))}
              >
                <SvgIcon fontSize="large" color="disabled">
                  <IconCircleMinus />
                </SvgIcon>
              </IconButton>
            </Stack>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
};
